import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Radio,
  Button,
} from "antd";
import { toast } from "react-toastify";
import {
  addNewTest,
  editChemistryTest,
  deleteChemistryTest,
} from "../../../actions/chemistry";
import ConfirmationModal from "../../Common/ConfirmationModal";
import Loading from "../../../assets/images/loading.gif";
import "./ChemAddNewTest.css";

const getInitialFormState = () => ({
  testName: "",
  unit: "",
  resultType: "integer",
  isPanelTest: true,
});

const normalizeResultType = (value) => {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return ["integer", "float"].includes(normalizedValue) ? normalizedValue : null;
};

const hasValidRequiredFields = ({ testName, resultType, isPanelTest }) => (
  testName.trim().length > 0
  && ["integer", "float"].includes(resultType)
  && typeof isPanelTest === "boolean"
);

const getFormStateFromTest = (testData) => {
  if (!testData) {
    return getInitialFormState();
  }

  return {
    testName: testData?.test || "",
    unit: testData?.units || "",
    resultType: normalizeResultType(testData?.result_type),
    isPanelTest: testData?.is_panel_test === 1,
  };
};

const ChemAddNewTest = (props) => {
  const {
    isOpen = true,
    handleHidePopup,
    getTestRules: refreshTestRules,
    mode = "create",
    initialTestData = null,
    canDeleteTest = true,
  } = props;
  const isEditMode = mode === "edit";
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formState, setFormState] = useState(getInitialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSaveConfirm(false);
      setShowDeleteConfirm(false);
      return;
    }

    setFormState(isEditMode ? getFormStateFromTest(initialTestData) : getInitialFormState());
    setShowSaveConfirm(false);
    setShowDeleteConfirm(false);
  }, [isOpen, isEditMode, initialTestData]);

  const updateFormField = (fieldName, fieldValue) => {
    setFormState((previousFormState) => ({
      ...previousFormState,
      [fieldName]: fieldValue,
    }));
  };

  const handleShowSaveConfirm = () => {
    if (!hasValidRequiredFields(formState)) {
      toast.warning("Please make sure all required fields are filled.");
      return;
    }

    setShowSaveConfirm(true);
  };

  const handleHideSaveConfirm = () => {
    if (isSubmitting) {
      return;
    }
    setShowSaveConfirm(false);
  };

  const handleShowDeleteConfirm = () => {
    if (!isEditMode || !canDeleteTest || isSubmitting) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleHideDeleteConfirm = () => {
    if (isSubmitting) {
      return;
    }
    setShowDeleteConfirm(false);
  };

  const handleCloseModal = (forceClose = false) => {
    if (!forceClose && isSubmitting) {
      return;
    }
    handleHidePopup();
  };

  const handleResultTypeChange = (event) => {
    updateFormField("resultType", event.target.value);
  };

  const handlePanelTestChange = (event) => {
    updateFormField("isPanelTest", event.target.value);
  };

  const toggleBodyLoadingState = (shouldShow = true) => {
    if (shouldShow) {
      document.body.classList = "ajax-loading";
    } else {
      document.body.classList = "";
    }
  };

  const resetSubmittingState = () => {
    toggleBodyLoadingState(false);
    setIsSubmitting(false);
  };

  const getResponseMessage = (response, fallbackMessage = "Something went wrong!") => {
    const responseMessage = response?.message;
    if (typeof responseMessage === "string" && responseMessage.trim().length > 0) {
      return responseMessage;
    }
    return fallbackMessage;
  };

  const handleSaveTest = async (input) => {
    try {
      toggleBodyLoadingState();
      setIsSubmitting(true);
      let response = null;

      if (isEditMode) {
        const isExistingSmartTest = initialTestData?.is_ml === true || initialTestData?.is_ml === 1;
        response = await editChemistryTest({
          id: initialTestData?.id,
          test: input.testName,
          genistaid_cell: isExistingSmartTest ? 0 : Number(initialTestData?.genistaid_cell || 0),
          result_cell: isExistingSmartTest ? "" : String(initialTestData?.result_cell || ""),
          units: input.unit || "",
          result_type: input.resultType || null,
          is_panel_test: isExistingSmartTest ? input.isPanelTest : null,
          is_ml: isExistingSmartTest,
        });
      } else {
        response = await addNewTest({
          testName: input.testName,
          unit: input.unit,
          resultType: input.resultType,
          isPanelTest: input.isPanelTest,
        });
      }

      if (response && response.success) {
        const responseMessage = getResponseMessage(response);
        const responseMessageLower = responseMessage.toLowerCase();
        if (responseMessageLower.includes("duplicate") || responseMessageLower.includes("database")) {
          toast.error(responseMessage);
          resetSubmittingState();
          return;
        }
        toggleBodyLoadingState(false);
        toast.success(responseMessage);
        refreshTestRules();
        setFormState(getInitialFormState());
        setShowSaveConfirm(false);
        setIsSubmitting(false);
        handleCloseModal(true);
      } else {
        resetSubmittingState();
        toast.error(getResponseMessage(response));
      }
    } catch (error) {
      resetSubmittingState();
      toast.error("Something went wrong!");
    }
  };

  const handleDeleteTest = async () => {
    const testId = initialTestData?.id;
    if (!testId) {
      toast.error("Unable to delete this test.");
      setShowDeleteConfirm(false);
      return;
    }

    try {
      toggleBodyLoadingState();
      setIsSubmitting(true);
      const response = await deleteChemistryTest(testId);
      if (response && response.success) {
        toggleBodyLoadingState(false);
        toast.success(getResponseMessage(response, "Test deleted successfully."));
        refreshTestRules();
        setShowDeleteConfirm(false);
        setIsSubmitting(false);
        handleCloseModal(true);
      } else {
        resetSubmittingState();
        toast.error(getResponseMessage(response));
      }
    } catch (error) {
      resetSubmittingState();
      toast.error("Something went wrong!");
    }
  };

  const {
    testName,
    unit,
    resultType,
    isPanelTest,
  } = formState;
  const modalTitle = isEditMode ? "Edit Chemistry Test" : "Add New Chemistry Test";
  const shouldUseSplitFooterLayout = isEditMode && canDeleteTest;
  let modalFooterButtons = (
    <>
      <Button onClick={handleCloseModal} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button type="primary" onClick={handleShowSaveConfirm} disabled={isSubmitting}>
        Create Test
      </Button>
    </>
  );
  if (isEditMode) {
    modalFooterButtons = (
      <>
        {canDeleteTest && (
          <Button danger onClick={handleShowDeleteConfirm} disabled={isSubmitting}>
            Delete
          </Button>
        )}
        <div className="ChemAddNewTest__footerActionsRight">
          <Button onClick={handleCloseModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleShowSaveConfirm} disabled={isSubmitting}>
            Update Test
          </Button>
        </div>
      </>
    );
  }

  return (
    <Modal
      open={isOpen}
      centered
      title={modalTitle}
      onCancel={handleCloseModal}
      footer={null}
      maskClosable={false}
      closable={!isSubmitting}
      destroyOnClose
      className="ChemAddNewTest__Modal"
    >
      <div className="ChemAddNewTest__popup">
        <div className="ChemAddNewTest__form">
          <div className="ChemAddNewTest__nameUnitRow">
            <div className="ChemAddNewTest__field ChemAddNewTest__field--grow">
              <label className="ChemAddNewTest__Label" htmlFor="ChemAddNewTest__inputTestName">
                Test Name
                <span className="ChemAddNewTest__required" aria-hidden="true">*</span>
              </label>
              <Input
                id="ChemAddNewTest__inputTestName"
                placeholder="Test Name"
                value={testName}
                onChange={(event) => updateFormField("testName", event.target.value)}
              />
            </div>
            <div className="ChemAddNewTest__field ChemAddNewTest__field--grow">
              <label className="ChemAddNewTest__Label" htmlFor="ChemAddNewTest__inputUnit">
                Unit
              </label>
              <Input
                id="ChemAddNewTest__inputUnit"
                placeholder="Unit"
                value={unit}
                onChange={(event) => updateFormField("unit", event.target.value)}
              />
            </div>
          </div>

          <div className="ChemAddNewTest__optionRow">
            <label className="ChemAddNewTest__Label" id="ChemAddNewTest__labelResultType">
              Result Type
              <span className="ChemAddNewTest__required" aria-hidden="true">*</span>
            </label>
            <Radio.Group
              value={resultType}
              onChange={handleResultTypeChange}
              aria-labelledby="ChemAddNewTest__labelResultType"
            >
              <Radio value="integer">Integer</Radio>
              <Radio value="float">Decimal</Radio>
            </Radio.Group>
          </div>

          <div className="ChemAddNewTest__optionRow">
            <label className="ChemAddNewTest__Label" id="ChemAddNewTest__labelPanelTest">
              Panel Test
              <span className="ChemAddNewTest__required" aria-hidden="true">*</span>
            </label>
            <Radio.Group
              value={isPanelTest}
              onChange={handlePanelTestChange}
              aria-labelledby="ChemAddNewTest__labelPanelTest"
            >
              <Radio value>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </div>
        </div>

        <div
          className={
            shouldUseSplitFooterLayout
              ? "ChemAddNewTest__footer ChemAddNewTest__footer--split"
              : "ChemAddNewTest__footer"
          }
        >
          {modalFooterButtons}
        </div>
      </div>
      <ConfirmationModal
        title={isEditMode ? "Update Test" : "Add Test"}
        bodyText={`Are you sure you want to ${isEditMode ? "update" : "add"} test <strong>${testName.trim()}</strong>?`}
        open={showSaveConfirm}
        className="ChemAddNewTest__ConfirmModal"
        okText="Confirm"
        cancelText="Cancel"
        onOk={() => handleSaveTest(formState)}
        onCancel={handleHideSaveConfirm}
        loading={isSubmitting}
      />
      <ConfirmationModal
        title="Delete Test"
        bodyText="Are you sure you want to delete this test?"
        open={showDeleteConfirm}
        className="ChemAddNewTest__ConfirmModal"
        okText="Confirm"
        cancelText="Cancel"
        onOk={handleDeleteTest}
        onCancel={handleHideDeleteConfirm}
        loading={isSubmitting}
      />
      <div className="overlay">
        <img src={Loading} alt="" />
      </div>
    </Modal>
  );
};

export default ChemAddNewTest;
